import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { useAuth } from '../store/auth';
import { Btn } from '../components/Btn';
import { Ic } from '../components/Icon';
import { Input } from '../components/Field';
import { Logo } from '../components/Logo';
import type { HttpError } from '../api/client';

export function LoginPage() {
  const setSession = useAuth((s) => s.setSession);
  const sessionExpired = useAuth((s) => s.sessionExpired);
  const clearSessionExpired = useAuth((s) => s.clearSessionExpired);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  useEffect(() => {
    return () => {
      clearSessionExpired();
    };
  }, [clearSessionExpired]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const login = useMutation({
    mutationFn: () => authApi.login({ email, password, rememberMe }),
    onSuccess: (data) => {
      setSession(data.token, data.user, rememberMe);
      navigate(from, { replace: true });
    },
  });

  const resend = useMutation({
    mutationFn: () => authApi.resendVerification(email),
  });

  const loginErr = login.error as HttpError | null;
  const needsVerification = loginErr?.body?.code === 'EMAIL_NOT_VERIFIED';

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };

  return (
    <div
      className="auth-shell"
      style={{
        minHeight: '100vh',
        background: '#fbfaf6',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        color: '#0f1e3d',
        overflow: 'hidden',
      }}
    >
      <div
        className="desktop-only"
        style={{
          background: '#0f1e3d',
          color: '#fff',
          padding: '48px 56px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
        }}
      >
        <div style={{ zIndex: 2 }}>
          <Logo size={80} textColor="#fff" subtitleColor="rgba(255,255,255,.6)" />
        </div>

        <div
          style={{
            position: 'absolute',
            right: -40,
            top: 80,
            fontFamily: 'Fraunces, serif',
            fontSize: 340,
            fontWeight: 700,
            color: 'rgba(244,185,58,.09)',
            letterSpacing: '-0.04em',
            lineHeight: 0.85,
            pointerEvents: 'none',
          }}
        >
          2026
        </div>

        <div style={{ zIndex: 2, maxWidth: 440 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'rgba(244,185,58,.15)',
              color: 'var(--accent)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.04em',
              marginBottom: 18,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--accent)' }} />
            FIFA WORLD CUP · USA · CAN · MEX
          </div>
          <h1
            className="serif"
            style={{ fontSize: 56, fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.02em', margin: 0 }}
          >
            Typuj mundial
            <br />
            z ekipą.
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.55, color: 'rgba(255,255,255,.68)', marginTop: 20 }}>
            Prywatne pokoje dla znajomych, publiczne ligi dla odważnych. Prosty scoring, jasne zasady,
            żadnego kombinowania.
          </p>
        </div>

        <div style={{ zIndex: 2, display: 'flex', gap: 28, color: 'rgba(255,255,255,.55)', fontSize: 12 }}>
          <Stat n="104" label="mecze" />
          <Stat n="48" label="drużyn" />
          <Stat n="3" label="kraje gospodarze" />
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className="auth-form"
        style={{
          padding: '72px 32px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
        <div className="mobile-only" style={{ marginBottom: 24 }}>
          <Logo size={56} />
        </div>
        <div
          style={{
            fontSize: 12,
            color: '#5b6578',
            letterSpacing: '.08em',
            fontWeight: 600,
            textTransform: 'uppercase',
          }}
        >
          Zaloguj się
        </div>
        <h2 className="serif" style={{ fontSize: 38, fontWeight: 700, margin: '6px 0 4px', letterSpacing: '-0.02em' }}>
          Witaj z powrotem
        </h2>
        <div style={{ fontSize: 14, color: '#5b6578', marginBottom: 32 }}>
          WCTyper jest tylko na zaproszenie. <Link to="/register" style={{ color: '#0f1e3d', fontWeight: 600, borderBottom: '1px solid #0f1e3d', paddingBottom: 1 }}>Masz kod?</Link>
        </div>

        {sessionExpired && (
          <div
            role="alert"
            style={{
              padding: 12,
              background: 'rgba(244,185,58,.18)',
              color: '#6b4a00',
              borderRadius: 10,
              fontSize: 13,
              marginBottom: 18,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              border: '1px solid rgba(244,185,58,.35)',
            }}
          >
            <Ic.lock size={14} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <b>Sesja wygasła.</b> Zaloguj się ponownie, żeby kontynuować od miejsca, w którym byłeś.
            </div>
          </div>
        )}

        <label style={fieldLabel}>E-MAIL</label>
        <Input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ty@example.com"
          style={{ marginBottom: 14 }}
        />

        <label style={fieldLabel}>HASŁO</label>
        <Input
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ marginBottom: 14 }}
        />

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
            color: '#5b6578',
            marginBottom: 18,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: '#0f1e3d', cursor: 'pointer' }}
          />
          Pozostań zalogowany
        </label>

        {login.isError && !needsVerification && (
          <div
            style={{
              padding: 12,
              background: 'rgba(208,70,60,.08)',
              color: '#8a2a22',
              borderRadius: 10,
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            {loginErr?.body?.message ?? 'Logowanie nie powiodło się'}
          </div>
        )}

        {needsVerification && (
          <div
            style={{
              padding: 14,
              background: 'rgba(244,185,58,.18)',
              color: '#6b4a00',
              borderRadius: 10,
              fontSize: 13,
              marginBottom: 14,
              border: '1px solid rgba(244,185,58,.35)',
              lineHeight: 1.5,
            }}
          >
            <b>Konto wymaga aktywacji.</b> Sprawdź swoją skrzynkę — wysłaliśmy do Ciebie link
            aktywacyjny przy rejestracji.
            {resend.isSuccess ? (
              <div style={{ marginTop: 8, color: '#0f6b34' }}>
                Wysłaliśmy nowy link na <b>{email}</b>. Sprawdź skrzynkę (i spam).
              </div>
            ) : (
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => resend.mutate()}
                  disabled={resend.isPending || !email}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: '#0f1e3d',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {resend.isPending ? 'Wysyłanie…' : 'Wyślij link aktywacyjny ponownie'}
                </button>
              </div>
            )}
          </div>
        )}

        <Btn type="submit" variant="primary" size="lg" fullWidth disabled={login.isPending}>
          {login.isPending ? 'Logowanie…' : 'Zaloguj się'}
        </Btn>

        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 13 }}>
          <Link to="/forgot-password" style={{ color: '#5b6578', textDecoration: 'none' }}>
            Zapomniałem hasła
          </Link>
        </div>

        <div
          style={{
            marginTop: 36,
            padding: 14,
            background: '#f7f6f2',
            borderRadius: 12,
            border: '1px solid rgba(15,30,61,.06)',
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#3a2a00',
              flexShrink: 0,
            }}
          >
            <Ic.lock size={16} />
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.5, color: '#5b6578' }}>
            <b style={{ color: '#0f1e3d' }}>Tylko na zaproszenie.</b> Dołącz, jeśli ktoś wysłał Ci kod
            e-mailem lub linkiem.
          </div>
        </div>
        </div>
      </form>
    </div>
  );
}

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: '#5b6578',
  fontWeight: 600,
  letterSpacing: '.04em',
  marginBottom: 6,
};

function Stat({ n, label }: { n: string; label: string }) {
  return (
    <div>
      <span className="serif" style={{ fontSize: 22, fontWeight: 700, color: '#fff', display: 'block' }}>
        {n}
      </span>
      {label}
    </div>
  );
}
